import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Code } from 'lucide-react';
import { projectsData } from '../data/projectsData';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
export const ProjectDetail = () => {
  const {
    id
  } = useParams<{
    id: string;
  }>();
  const navigate = useNavigate();
  const project = projectsData.find(p => p.id === Number(id));
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);
  if (!project) {
    return <div className="min-h-screen flex items-center justify-center" data-id="element-229">
        <div className="text-center" data-id="element-230">
          <h1 className="text-4xl font-bold mb-4" data-id="element-231">Project Not Found</h1>
          <Link to="/" className="text-indigo-600 hover:text-indigo-800 transition-colors" data-id="element-232">
            Return to Home
          </Link>
        </div>
      </div>;
  }
  return <div className="font-sans bg-white text-gray-800" data-id="element-233">
      <Header data-id="element-234" />
      <main className="pt-20" data-id="element-235">
        {/* Hero Section */}
        <section className="py-12 bg-gradient-to-br from-gray-50 to-white" data-id="element-236">
          <div className="container mx-auto px-4 md:px-6" data-id="element-237">
            <button onClick={() => navigate('/')} className="inline-flex items-center text-indigo-600 hover:text-indigo-800 transition-colors mb-8 group" data-id="element-238">
              <ArrowLeft size={20} className="mr-2 group-hover:-translate-x-1 transition-transform" data-id="element-239" />
              Back to Projects
            </button>

            <div className="mb-8" data-id="element-240">
              <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium mb-4 ${project.category === 'web' ? 'bg-blue-100 text-blue-800' : project.category === 'mobile' ? 'bg-green-100 text-green-800' : 'bg-purple-100 text-purple-800'}`} data-id="element-241">
                {project.category === 'web' ? 'Web Design' : project.category === 'mobile' ? 'Mobile App' : 'Branding'}
              </span>
              <h1 className="text-4xl md:text-5xl font-bold mb-4" data-id="element-242">
                {project.title}
              </h1>
              <p className="text-xl text-gray-600 max-w-3xl" data-id="element-243">
                {project.description}
              </p>
            </div>

            {/* Project Meta */}
            <div className="flex flex-wrap gap-6 mb-8" data-id="element-244">
              {project.year && <div className="flex items-center text-gray-600" data-id="element-245">
                  <Calendar size={20} className="mr-2 text-indigo-600" data-id="element-246" />
                  <span data-id="element-247">{project.year}</span>
                </div>}
              {project.role && <div className="flex items-center text-gray-600" data-id="element-248">
                  <User size={20} className="mr-2 text-indigo-600" data-id="element-249" />
                  <span data-id="element-250">{project.role}</span>
                </div>}
              {project.client && <div className="flex items-center text-gray-600" data-id="element-251">
                  <span className="font-medium mr-2" data-id="element-252">Client:</span>
                  <span data-id="element-253">{project.client}</span>
                </div>}
            </div>
          </div>
        </section>

        {/* Main Image */}
        <section className="py-12" data-id="element-254">
          <div className="container mx-auto px-4 md:px-6" data-id="element-255">
            <div className="rounded-lg overflow-hidden shadow-2xl" data-id="element-256">
              <img src={project.image} alt={project.title} className="w-full h-auto object-cover" data-id="element-257" />
            </div>
          </div>
        </section>

        {/* Project Details */}
        <section className="py-12 bg-gray-50" data-id="element-258">
          <div className="container mx-auto px-4 md:px-6" data-id="element-259">
            <div className="max-w-4xl mx-auto" data-id="element-260">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-12" data-id="element-261">
                {/* Main Content */}
                <div className="md:col-span-2" data-id="element-262">
                  <h2 className="text-3xl font-bold mb-6" data-id="element-263">Project Overview</h2>
                  <p className="text-gray-600 text-lg leading-relaxed mb-8" data-id="element-264">
                    {project.fullDescription || project.description}
                  </p>

                  <h3 className="text-2xl font-bold mb-4" data-id="element-265">Key Features</h3>
                  <ul className="space-y-3 text-gray-600" data-id="element-266">
                    <li className="flex items-start" data-id="element-267">
                      <span className="inline-block w-2 h-2 bg-indigo-600 rounded-full mt-2 mr-3" data-id="element-268"></span>
                      <span data-id="element-269">
                        Intuitive user interface designed for optimal user
                        experience
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-270">
                      <span className="inline-block w-2 h-2 bg-indigo-600 rounded-full mt-2 mr-3" data-id="element-271"></span>
                      <span data-id="element-272">
                        Responsive design that works seamlessly across all
                        devices
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-273">
                      <span className="inline-block w-2 h-2 bg-indigo-600 rounded-full mt-2 mr-3" data-id="element-274"></span>
                      <span data-id="element-275">
                        Modern visual design following current design trends
                      </span>
                    </li>
                    <li className="flex items-start" data-id="element-276">
                      <span className="inline-block w-2 h-2 bg-indigo-600 rounded-full mt-2 mr-3" data-id="element-277"></span>
                      <span data-id="element-278">
                        Accessibility-focused implementation for inclusive user
                        experience
                      </span>
                    </li>
                  </ul>
                </div>

                {/* Sidebar */}
                <div className="md:col-span-1" data-id="element-279">
                  {project.technologies && project.technologies.length > 0 && <div className="bg-white p-6 rounded-lg shadow-md mb-6" data-id="element-280">
                      <div className="flex items-center mb-4" data-id="element-281">
                        <Code size={20} className="text-indigo-600 mr-2" data-id="element-282" />
                        <h3 className="text-xl font-bold" data-id="element-283">Technologies</h3>
                      </div>
                      <div className="flex flex-wrap gap-2" data-id="element-284">
                        {project.technologies.map((tech, index) => <span key={index} className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm" data-id="element-285">
                            {tech}
                          </span>)}
                      </div>
                    </div>}

                  <div className="bg-white p-6 rounded-lg shadow-md" data-id="element-286">
                    <h3 className="text-xl font-bold mb-4" data-id="element-287">Project Info</h3>
                    <div className="space-y-3 text-sm" data-id="element-288">
                      <div data-id="element-289">
                        <span className="font-medium text-gray-700" data-id="element-290">
                          Category:
                        </span>
                        <p className="text-gray-600 capitalize" data-id="element-291">
                          {project.category}
                        </p>
                      </div>
                      {project.year && <div data-id="element-292">
                          <span className="font-medium text-gray-700" data-id="element-293">
                            Year:
                          </span>
                          <p className="text-gray-600" data-id="element-294">{project.year}</p>
                        </div>}
                      {project.role && <div data-id="element-295">
                          <span className="font-medium text-gray-700" data-id="element-296">
                            Role:
                          </span>
                          <p className="text-gray-600" data-id="element-297">{project.role}</p>
                        </div>}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-white" data-id="element-298">
          <div className="container mx-auto px-4 md:px-6 text-center" data-id="element-299">
            <h2 className="text-3xl font-bold mb-4" data-id="element-300">
              Interested in working together?
            </h2>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto" data-id="element-301">
              I'm always open to discussing new projects and creative
              opportunities.
            </p>
            <Link to="/#contact" className="inline-flex items-center justify-center px-8 py-3 bg-indigo-600 text-white font-medium rounded-md hover:bg-indigo-700 transition-colors" data-id="element-302">
              Get In Touch
            </Link>
          </div>
        </section>
      </main>
      <Footer data-id="element-303" />
    </div>;
};